import { makeAutoObservable } from 'mobx';
import { HistoryOrderData } from '../../../models/fixed/data-analysis';
import { SortType, dateFormat, formatEatTypeText, handleSort } from '../../../constants/tool-order';
import { Order } from '../../../models/order.entity';
import { Dish } from '../../../models/dish.entity';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import apiManage from '../../../request';
import {
    applyHistoryOrderDetailAmountFallback,
    cloneOrderDetailTableRowFallback,
    mergeOrderDetailSummaryWithTableRow,
    parseDataAnalysisOrderDetailWithHead,
} from '../../../request/api/data-analysis';
import { WhenError } from '../../../request/http';
import i18n from '../../../i18n';
import GlobalNotification from '../../../component/common/snackbar';
import { exportExcel, formatEUR } from '../../../constants/tool.fn';
import { FetchCancelGate } from '../../../utils/fetch-cancel-gate';


/**
 * 历史订单数据 mobx；列表只按日期拉全量，桌台/订单等筛选在页面本地完成。
 */
class HistoryOrderDataState {

    /** 列表请求取消门闩（日期快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();

    constructor() {
        makeAutoObservable(this);
    }

    /** 列表拉数中（仅日期/首载触发请求） */
    loading = false;
    // 历史订单订单数据
    data: HistoryOrderData = {
        dineOrder: [],
        takeoutOrder: []
    };
    isOpenOrderDetail = false
    /** 打开详情时仅保留 server_id 等请求参数 */
    orderDetailData: Order = {}
    /** 详情摘要：默认列表行，接口 `order_head` 有值字段再覆盖 */
    orderDetailSummary: Order | null = null
    orderDetailDish: Dish[] = []
    detailLoading = true
    errorMessage = ''
    sortKeyValue: {
        hisEatInArr?: SortType;
        hisEatOutArr?: SortType;
    } = {}
    dataSource: HistoryOrderData = {
        dineOrder: [],
        takeoutOrder: []
    };

    /**
     * 获取数据（不带服务端筛选；与导出一致为日期范围内全量）
     */
    async fetchData(stringId: string, startDate: string, endDate: string) {
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.loading = true;
        this.errorMessage = '';
        try {
            const res = await apiManage.get_data_analysis_res_history_order({
                string_id: stringId,
                start: startDate,
                end: endDate,
            }, { cancelToken });
            if (!this.listFetchGate.isLatest(seq)) {
                return;
            }
            if (!res?.error) {
                /** 列表直接使用接口数据；旧版 `formatDataRate`/`totalMaxRate` 已无消费方，不再写入 */
                this.setData(res);
                this.setErrorMessage('');
            } else {
                const error = res as WhenError;
                if (error.message !== 'canceled') {
                    if (error?.result?.statusCode === 404) {
                        this.setErrorMessage(i18n.t('business_analysis_page_text_restaurant_data_not_synchronized'));
                    } else {
                        GlobalNotification.error(i18n.t('history_order_data_page_tips_failed_to_obtain_historical_order_data'));
                    }
                    this.setData({ dineOrder: [], takeoutOrder: [] });
                }
            }
        } finally {
            this.listFetchGate.endLoading(seq, (value) => {
                this.loading = value;
            });
        }
    }

    /**
     * 使用页面传入的两表行数据分别导出为 xlsx（与表格展示一致，含本地筛选/排序），不重复请求接口
     */
    downloadDineAndTakeoutExcels(lists: { dineOrder: Order[]; takeoutOrder: Order[] }) {
        const downloadData: HistoryOrderData = {
            dineOrder: lists.dineOrder,
            takeoutOrder: lists.takeoutOrder,
        };
        (['dineOrder', 'takeoutOrder'] as const).forEach((key) => {
            if (downloadData[key].length) {
                const { newBuildData, colWpxData } = this.buildDownloadSheetData(downloadData, key);
                const fileName =
                    key === 'dineOrder'
                        ? `${i18n.t('immediate_open_dine_in_order')}.xlsx`
                        : `${i18n.t('immediate_closed_takeaway_orders')}.xlsx`;
                exportExcel(newBuildData, fileName, colWpxData);
            } else {
                GlobalNotification.warning(i18n.t('general_text_no_data'));
            }
        });
    }

    buildDownloadSheetData(downloadData: HistoryOrderData, key: keyof HistoryOrderData) {
        const colWpxData = [{ wpx: 80 }, { wpx: 80 }, { wpx: 80 }, { wpx: 100 }, { wpx: 100 }, { wpx: 100 }, { wpx: 100 }];
        const headData = {
            dineOrder: [[
                i18n.t('business_analysis_page_order_account'),
                i18n.t('immediate_order_number'),
                i18n.t('business_analysis_page_table_name'),
                i18n.t('immediate_number_people'),
                i18n.t('immediate_actual_payment'),
                i18n.t('immediate_table_opening_time'),
                i18n.t('immediate_statement_time'),
                i18n.t('immediate_opener'),
            ]],
            takeoutOrder: [[
                i18n.t('business_analysis_page_order_account'),
                i18n.t('immediate_order_type'),
                i18n.t('immediate_actual_payment'),
                i18n.t('immediate_billing_time'),
                i18n.t('immediate_due_time'),
                i18n.t('immediate_statement_time'),
                i18n.t('immediate_billing_staff'),
            ]],
        }
        const tableData = key === 'dineOrder' ?
            downloadData['dineOrder'].map(item => [
                String(item?.order_head_id) || '',
                String(item?.check_id) || '',
                item?.table_name || '',
                String(item?.customer_num) || '',
                formatEUR(+(item?.actual_amount || 0)),
                dateFormat(String(item?.order_start_time ?? '')),
                dateFormat(String(item?.order_end_time ?? '')),
                item?.order_employee_name || '',
            ])
            :
            downloadData['takeoutOrder'].map(item => [
                String(item?.order_head_id) || '',
                formatEatTypeText(+(item?.eat_type || 0)),
                formatEUR(+(item?.actual_amount || 0)),
                dateFormat(String(item?.order_start_time ?? '')),
                item?.kds_time ? dateFormat(String(item.kds_time)) : '',
                dateFormat(String(item?.order_end_time ?? '')),
                item?.order_employee_name || '',
            ]);
        const newBuildData = headData[key].concat(tableData);
        return {
            newBuildData,
            colWpxData: key === 'dineOrder' ? colWpxData.splice(colWpxData.length, 1, { wpx: 100 }) : colWpxData
        }
    }

    async fetchDetailData() {
        this.setDetailLoading(true);
        const mainPageData = MobxObjectsManager.get(MainPageState);
        const res = await apiManage.get_data_analysis_res_history_order_details({
            string_id: mainPageData.currentRestaurant?.string_id || '',
            id: `${this.orderDetailData.server_id || ''}`,
        });
        const { order, details } = parseDataAnalysisOrderDetailWithHead(res);
        if (!res?.error) {
            const apiOrder = applyHistoryOrderDetailAmountFallback(order);
            this.setOrderDetailSummary(
                mergeOrderDetailSummaryWithTableRow(this.orderDetailData, apiOrder)
            );
            this.setOrderDetailDish(details ?? []);
        } else {
            GlobalNotification.error(i18n.t('immediate_get_order_details_failed'));
        }
        this.setDetailLoading(false)
    }

    setData(value: HistoryOrderData) {
        this.data = value
        this.dataSource = { ...value }
    }

    setOpenOrderDetail(value: boolean) {
        this.isOpenOrderDetail = value
        if (!value) {
            this.orderDetailSummary = null
            this.orderDetailDish = []
        }
    }

    setOrderDetailData(value: Order) {
        this.orderDetailData = value
        this.orderDetailSummary = cloneOrderDetailTableRowFallback(value)
    }

    setOrderDetailSummary(value: Order | null) {
        this.orderDetailSummary = value
    }

    setOrderDetailDish(value: Dish[]) {
        this.orderDetailDish = value
    }

    setDetailLoading(value: boolean) {
        this.detailLoading = value
    }

    setErrorMessage(value: string) {
        this.errorMessage = value
    }

    setEatInArrSort(value: SortType, key: keyof HistoryOrderData) {
        this.sortKeyValue[key] = value
    }

    setDataSort(key: keyof HistoryOrderData) {
        const value = this.data[key]
        const sortValue = this.sortKeyValue[key]?.value
        const sortKey = this.sortKeyValue[key]?.key
        if (sortValue && sortKey) {
            const valueSort = handleSort({ value: sortValue, key: sortKey }, [...value], 'Date') || []
            this.data[key] = valueSort
        } else {
            this.data[key] = [...this.dataSource[key]]
        }
    }
}

export default HistoryOrderDataState;
